import {
  getDocs,
  onSnapshot,
  type Query,
  type QuerySnapshot,
  type QueryDocumentSnapshot,
  type DocumentSnapshot,
  type DocumentReference,
  getDoc,
  where,
  query as newQuery,
  or,
} from 'firebase/firestore';
import {
  ref,
  watch,
  type Ref,
  onScopeDispose,
  type UnwrapRef,
  getCurrentScope,
  type MaybeRefOrGetter,
  watchEffect,
  toValue,
  effectScope,
  toRef,
  onMounted,
  onUnmounted,
  computed,
  type ComputedRef,
} from 'vue';
import type { Unsubscribe } from './actions';
import { dateNow } from '../../cross-app-shared/services/dates-and-durations.ts';
import type { Uid } from '../../cross-app-shared/types/common';
import { usersByUidRef } from '@/services/users';
import { arrayToMap } from '../../compliance-shared/services/utility';

export const isMobile = ref(false);
if (!SERVER_SIDE) {
  // See also, the media query in src/App.css:
  const smallWidthMediaMatch = window.matchMedia('(max-width: 600px)');

  const isMobileUpdate = () => {
    isMobile.value = smallWidthMediaMatch.matches;
  };
  isMobileUpdate();
  smallWidthMediaMatch.addEventListener('change', () => isMobileUpdate());
}

export const isNarrow = ref(false);
if (!SERVER_SIDE) {
  // This matches up with Bootstrap's lg breakpoint:
  const narrowMediaMatch = window.matchMedia('(max-width: 991px)');
  const isNarrowUpdate = () => {
    isNarrow.value = narrowMediaMatch.matches;
  };
  isNarrowUpdate();
  narrowMediaMatch.addEventListener('change', () => isNarrowUpdate());
}

export const isSmallCalendar = ref(false);
if (!SERVER_SIDE) {
  const smallCalendarMediaMatch = window.matchMedia('(max-width: 1200px)');
  const isSmallCalendarUpdate = () => {
    isSmallCalendar.value = smallCalendarMediaMatch.matches;
  };
  isSmallCalendarUpdate();
  smallCalendarMediaMatch.addEventListener('change', () => isSmallCalendarUpdate());
}

export const isPrint = ref(false);
if (!SERVER_SIDE) {
  const printMediaMatch = window.matchMedia('print');
  const printMediaUpdate = () => {
    isPrint.value = printMediaMatch.matches;
  };
  printMediaUpdate();
  printMediaMatch.addEventListener('change', () => printMediaUpdate());
}

export interface ExposedPromise<PromiseType> {
  promise: Promise<PromiseType>;
  resolve: (value: PromiseType | PromiseLike<PromiseType>) => void;
  reject: (reason?: any) => void;
}

export function exposedPromise<PromiseType>(): ExposedPromise<PromiseType> {
  const exposedPromise: any = {
    promise: null,
    resolve: null,
    reject: null,
  };

  exposedPromise.promise = new Promise<PromiseType>(function (resolve, reject) {
    exposedPromise.resolve = resolve;
    exposedPromise.reject = reject;
  });

  return exposedPromise;
}

export function snapshotToRef<InputType, ResultType>(
  watching: InputType extends any[] ? { [K in keyof InputType]: Ref<InputType[K]> } : Ref<InputType>,
  defaultValue: ResultType,
  func: (input: InputType, output: Ref<UnwrapRef<ResultType>>) => Unsubscribe,
): Ref<UnwrapRef<ResultType>> {
  let unsubscribe: Unsubscribe = () => {};
  const result = ref(defaultValue) as Ref<UnwrapRef<ResultType>>;

  watch(
    watching,
    (newWatching) => {
      unsubscribe();
      result.value = defaultValue as any;
      unsubscribe = func(newWatching as any, result);
    },
    { immediate: true },
  );

  if (getCurrentScope()) {
    onScopeDispose(() => {
      unsubscribe();
    });
  }

  return result;
}

export interface UseQueryAsArray<DocumentType extends object> {
  documents: Ref<DocumentType[]>;
  isLoading: Ref<boolean>;
}

export function useQueryAsArray<DocumentType extends object>(
  query: MaybeRefOrGetter<Query<DocumentType, DocumentType> | null>,
  once: boolean = false,
  isDelayed = false,
  callback: ((documents: DocumentType[]) => DocumentType[]) | null = null,
): UseQueryAsArray<DocumentType> {
  const documents: Ref<DocumentType[]> = ref([]);
  const isLoading: Ref<boolean> = ref(true);
  let unsubscribe: Unsubscribe = function () {};

  if (getCurrentScope()) {
    onScopeDispose(function () {
      if (isDelayed) {
        console.error(
          'Unsubscribing from useQueryAsArrayDelayed query! (which is expected to be global and persistent)',
        );
      }
      unsubscribe();
    });
  }

  function querySnapshotHandler(querySnapshot: QuerySnapshot<DocumentType, DocumentType>) {
    const documentArray: DocumentType[] = [];

    for (const documentSnapshot of querySnapshot.docs) {
      documentArray.push(documentSnapshot.data());
    }

    if (callback !== null) {
      documents.value = callback(documentArray);
    } else {
      documents.value = documentArray;
    }

    isLoading.value = false;
  }

  watchEffect(function () {
    const queryValue = toValue(query);
    unsubscribe();
    documents.value = [];
    isLoading.value = true;
    unsubscribe = function () {};

    if (queryValue === null) {
      isLoading.value = false;
    } else {
      if (once) {
        getDocs(queryValue).then(querySnapshotHandler);
      } else {
        unsubscribe = onSnapshot(queryValue, querySnapshotHandler);
      }
    }
  });

  return { documents, isLoading };
}

export function useDocumentReference<DocumentType extends object>(
  documentReference: MaybeRefOrGetter<DocumentReference<DocumentType, DocumentType> | null>,
  once: boolean = false,
): Ref<DocumentType | null> {
  const documentVueReference: Ref<DocumentType | null> = ref(null);
  let unsubscribe: Unsubscribe = function () {};

  if (getCurrentScope()) {
    onScopeDispose(function () {
      unsubscribe();
    });
  }

  function querySnapshotHandler(documentSnapshot: DocumentSnapshot<DocumentType, DocumentType>) {
    if (documentSnapshot.exists()) {
      documentVueReference.value = documentSnapshot.data()!;
    } else {
      documentVueReference.value = null;
    }
  }

  watch(
    toRef(documentReference),
    function (newDocumentRef, oldDocumentRef) {
      if (newDocumentRef?.path !== oldDocumentRef?.path) {
        unsubscribe();
        documentVueReference.value = null;

        if (once) {
          unsubscribe = function () {};

          if (newDocumentRef) {
            getDoc<DocumentType, DocumentType>(newDocumentRef).then(querySnapshotHandler);
          }
        } else if (newDocumentRef) {
          unsubscribe = onSnapshot(newDocumentRef, querySnapshotHandler);
        }
      }
    },
    { immediate: true },
  );

  return documentVueReference;
}

export function useSnapshot<DocumentType extends object>(
  documentReference: MaybeRefOrGetter<DocumentReference<DocumentType, DocumentType> | null>,
  once: boolean = false,
): Ref<DocumentSnapshot<DocumentType, DocumentType> | null> {
  const documentSnapshotVueReference: Ref<DocumentSnapshot<DocumentType, DocumentType> | null> = ref(null);
  let unsubscribeHandler: Unsubscribe = function () {};

  function unsubscribe() {
    unsubscribeHandler();
    unsubscribeHandler = () => {};
  }

  if (getCurrentScope()) {
    onScopeDispose(unsubscribe);
  }

  function querySnapshotHandler(documentSnapshot: DocumentSnapshot<DocumentType, DocumentType>) {
    documentSnapshotVueReference.value = documentSnapshot;
  }

  watchEffect(function () {
    const documentRef = toValue(documentReference);
    unsubscribe();
    documentSnapshotVueReference.value = null;

    if (documentRef) {
      if (once) {
        getDoc(documentRef).then(querySnapshotHandler);
      } else {
        unsubscribeHandler = onSnapshot(documentRef, querySnapshotHandler);
      }
    }
  });

  return documentSnapshotVueReference;
}

export function useQueryAsArrayDelayed<DocumentType extends object>(
  query: MaybeRefOrGetter<Query<DocumentType, DocumentType>>,
  once: boolean = false,
  callback: ((documents: DocumentType[]) => DocumentType[]) | null = null,
): () => UseQueryAsArray<DocumentType> {
  return delayedDetachedEffectScope(() => useQueryAsArray(query, once, true, callback));
}

export interface SortOption<DataType> {
  value: string;
  label: string;
  labelLeft: string;
  labelRight: string;
  sort: (a: DataType, b: DataType) => number;
  enabled?: () => boolean;
}

/**
 * Given an array of async functions, this function executes 10 at a time in parallel.
 * A single function is run at first just in case the batch is destined to fail immediately.
 */
export async function batchAsync(batchSize: number, funcs: (() => Promise<any>)[]) {
  if (funcs.length === 0) {
    return;
  }

  funcs = funcs.slice();
  // I'd rather have 1 initial failure than 10 initial failures, so start with just one:
  await funcs.pop()!();

  const promises: Promise<any>[] = [];
  for (let i = 0; i < batchSize; i++) {
    promises.push(
      (async () => {
        while (funcs.length > 0) {
          await funcs.pop()!();
        }
      })(),
    );
  }

  await Promise.all(promises);
}

export function animationFramesToSeconds(startMs: Ref<number>): Ref<number> {
  let secondsLastSeen = 0;
  let looping = false;
  const result = ref(0);

  watch(
    startMs,
    () => {
      result.value = Math.floor((dateNow().getTime() - startMs.value) / 1000);
    },
    { immediate: true },
  );

  function loop() {
    const diffMs = dateNow().getTime() - startMs.value;
    const secondsNow = Math.floor(diffMs / 1000);
    if (secondsLastSeen !== secondsNow) {
      secondsLastSeen = secondsNow;
      result.value = secondsNow;
    }

    if (looping) {
      requestAnimationFrame(loop);
    }
  }

  onMounted(() => {
    looping = true;
    requestAnimationFrame(loop);
  });

  onUnmounted(() => {
    looping = false;
  });

  return result;
}

export function cloneObject<ObjectType extends object | null>(object: ObjectType): ObjectType {
  return JSON.parse(JSON.stringify(object));
}

export function multiQueryIn<DocumentType extends object>(
  query: Query<DocumentType, DocumentType>,
  field: string,
  valueArray: any[],
  callback: (querySnapshots: QuerySnapshot<DocumentType, DocumentType>[]) => void,
  includeNull: boolean = false,
): Unsubscribe {
  const querySnapshots: (QuerySnapshot<DocumentType, DocumentType> | null)[] = [];
  const unsubscribes: Unsubscribe[] = [];

  function setupListener(query: Query<DocumentType, DocumentType>, index: number) {
    querySnapshots.push(null);

    unsubscribes.push(
      onSnapshot(query, function (querySnapshot) {
        querySnapshots[index] = querySnapshot;

        for (const querySnapshot of querySnapshots) {
          if (querySnapshot === null) {
            return;
          }
        }

        callback(querySnapshots as QuerySnapshot<DocumentType, DocumentType>[]);
      }),
    );
  }

  for (let index = 0; index * 10 < valueArray.length; index++) {
    const valueArraySlice = valueArray.slice(index * 10, (index + 1) * 10);
    const valueWhere = where(field as any, 'in', valueArraySlice);

    if (includeNull && index === 0) {
      setupListener(newQuery(query, or(valueWhere, where(field as any, '==', null))), index);
    } else {
      setupListener(newQuery(query, valueWhere), index);
    }
  }

  let unsubscribed = false;
  if (valueArray.length === 0) {
    setTimeout(() => {
      if (!unsubscribed) {
        callback([]);
      }
    }, 0);
  }

  return function () {
    unsubscribed = true;
    for (const unsubscribe of unsubscribes) {
      unsubscribe();
    }
  };
}

export function useMultiQueryIn<DocumentType extends object, DocumentTypeTransformed extends object = DocumentType>(
  queryRef: MaybeRefOrGetter<Query<DocumentType, DocumentType> | null>,
  fieldRef: MaybeRefOrGetter<string>,
  valueArrayRef: MaybeRefOrGetter<any[]>,
  isDelayed: boolean = false,
  postProcessor:
    | ((documents: DocumentType[], snapshots: QueryDocumentSnapshot<DocumentType>[]) => DocumentTypeTransformed[])
    | null = null,
  includeNull: boolean = false,
): UseQueryAsArray<DocumentTypeTransformed> {
  const documents: Ref<DocumentTypeTransformed[]> = ref([]);
  const isLoading: Ref<boolean> = ref(true);
  let unsubscribe: Unsubscribe = () => {};

  if (getCurrentScope()) {
    onScopeDispose(function () {
      if (isDelayed) {
        console.error(
          'Unsubscribing from useMultiQueryInDelayed query! (which is expected to be global and persistent)',
        );
      }
      unsubscribe();
    });
  }

  watchEffect(function () {
    const queryValue = toValue(queryRef);
    documents.value = [];
    isLoading.value = true;
    unsubscribe();
    unsubscribe = () => {};

    if (queryValue === null) {
      isLoading.value = false;
    } else {
      unsubscribe = multiQueryIn(
        queryValue,
        toValue(fieldRef),
        toValue(valueArrayRef),
        function (querySnapshots: QuerySnapshot<DocumentType, DocumentType>[]) {
          const newDocuments: DocumentType[] = [];
          const newSnapshots: QueryDocumentSnapshot<DocumentType>[] = [];

          for (const querySnapshot of querySnapshots) {
            for (const documentSnapshot of querySnapshot.docs) {
              newDocuments.push(documentSnapshot.data());
              newSnapshots.push(documentSnapshot);
            }
          }

          if (postProcessor) {
            documents.value = postProcessor(newDocuments, newSnapshots);
          } else {
            // If no postProcessor function exists DocumentTypeTransformed === DocumentType:
            documents.value = newDocuments as any as DocumentTypeTransformed[];
          }

          isLoading.value = false;
        },
        includeNull,
      );
    }
  });

  return { documents, isLoading };
}

/**
 * This function allows us to defer global queries until they're actually used. Once used, the queries will remain
 * active (because they're run in a detached effect scope) allowing us to switch between pages without waiting for
 * data to re-load:
 */
export function delayedDetachedEffectScope<T>(initialize: () => T): () => T {
  const scope = effectScope(true);
  let results: T | null = null;

  return () => {
    if (results === null) {
      scope.run(() => {
        results = initialize();
      });
    }

    return results!;
  };
}

export function useMultiQueryInDelayed<DocumentType extends object>(
  queryRef: MaybeRefOrGetter<Query<DocumentType, DocumentType> | null>,
  fieldRef: MaybeRefOrGetter<string>,
  valueArrayRef: MaybeRefOrGetter<any[]>,
  postProcessor: ((documents: DocumentType[]) => DocumentType[]) | null = null,
): () => UseQueryAsArray<DocumentType> {
  return delayedDetachedEffectScope(() => useMultiQueryIn(queryRef, fieldRef, valueArrayRef, true, postProcessor));
}

export function useArrayAsMap<T, K extends string>(arr: Ref<T[]>, field: keyof T): ComputedRef<Record<K, T>> {
  return computed(() => {
    return arrayToMap(arr.value, field);
  });
}

// Replaces strings like {uid:abc...} and replaces them with the user's name (or "somebody" if not found):
export function replaceUidVars(msg: string) {
  return msg.replace(/\{uid:[a-zA-Z0-9]+\}/g, (substr) => {
    const uid = substr.replace('{uid:', '').replace('}', '') as Uid;
    if (uid === 'system') {
      return 'System';
    }

    const user = usersByUidRef.value.get(uid);
    return user?.name || user?.email || 'somebody';
  });
}

// Modified version of https://stackoverflow.com/a/16348977:
export function stringToColor(str: string): string {
  let hash = 0;
  for (const char of str.split('')) {
    hash = char.charCodeAt(0) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += value.toString(16).padStart(2, '0');
  }
  return color;
}
